import React from 'react';
import { createStyles } from 'antd-style';

interface ColorToken {
  name: string;
  value: string;
}

interface ColorSection {
  title: string;
  tokens: ColorToken[];
}

const colorSections: ColorSection[] = [
  {
    title: 'Main Blue',
    tokens: [{ name: 'Indigo-default', value: '#2D40CB' }],
  },
  {
    title: 'Secondary Blue',
    tokens: [
      { name: 'Blue-50', value: '#F3F7FF' },
      { name: 'Blue-100', value: '#E0ECFF' },
      { name: 'Blue-200', value: '#C7DBFF' },
      { name: 'Blue-300', value: '#A3C4FF' },
      { name: 'Blue-400', value: '#7AA8FF' },
      { name: 'Blue-500', value: '#5088FF' },
      { name: 'Blue-600', value: '#2D6AE8' },
      { name: 'Blue-700', value: '#070A1F' },
    ],
  },
  {
    title: 'Yellow',
    tokens: [
      { name: 'Yellow-Light', value: '#FFFBEB' },
      { name: 'Yellow-Light2', value: '#FEF3C7' },
      { name: 'Yellow-Medium', value: '#FBBF24' },
      { name: 'Yellow-Dark', value: '#B45309' },
    ],
  },
  {
    title: 'Lime',
    tokens: [
      { name: 'Lime-Light', value: '#F7FDE2' },
      { name: 'Lime-Light2', value: '#D9F99D' },
      { name: 'Lime-Medium', value: '#84CC16' },
      { name: 'Lime-Dark', value: '#365314' },
    ],
  },
  {
    title: 'Green',
    tokens: [
      { name: 'Green-Light', value: '#F0FDF4' },
      { name: 'Green-Light2', value: '#DCFCE7' },
      { name: 'Green-Medium', value: '#4ADE80' },
      { name: 'Green-Dark', value: '#15803D' },
    ],
  },
  {
    title: 'Teal',
    tokens: [
      { name: 'Teal-Light', value: '#F0FDFA' },
      { name: 'Teal-Light2', value: '#CCFBF1' },
      { name: 'Teal-Medium', value: '#2DD4BF' },
      { name: 'Teal-Dark', value: '#115E59' },
    ],
  },
  {
    title: 'Purple',
    tokens: [
      { name: 'Purple-Light', value: '#FAF5FF' },
      { name: 'Purple-Light2', value: '#E9D5FF' },
      { name: 'Purple-Medium', value: '#A855F7' },
      { name: 'Purple-Dark', value: '#581C87' },
    ],
  },
  {
    title: 'Pink',
    tokens: [
      { name: 'Pink-Light', value: '#FDF2F8' },
      { name: 'Pink-Light2', value: '#FBCFE8' },
      { name: 'Pink-Medium', value: '#F472B6' },
      { name: 'Pink-Dark', value: '#831843' },
    ],
  },
  {
    title: 'Red',
    tokens: [
      { name: 'Red-Light', value: '#FEF2F2' },
      { name: 'Red-Light2', value: '#FECACA' },
      { name: 'Red-Medium', value: '#F87171' },
      { name: 'Red-Dark', value: '#7F1D1D' },
    ],
  },
  {
    title: 'Gray',
    tokens: [
      { name: 'Gray-0', value: '#FDFDFD' },
      { name: 'Gray-50', value: '#F5F6F7' },
      { name: 'Gray-100', value: '#EBEDF0' },
      { name: 'Gray-200', value: '#D4D7DD' },
      { name: 'Gray-300', value: '#BBBFC7' },
      { name: 'Gray-400', value: '#A3A8B2' },
      { name: 'Gray-500', value: '#898E9A' },
      { name: 'Gray-600', value: '#6E7380' },
      { name: 'Gray-700', value: '#555861' },
      { name: 'Gray-800', value: '#3A3C42' },
      { name: 'Gray-900', value: '#141414' },
    ],
  },
];

const useStyles = createStyles(({ css }) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: 64px;
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: 32px;
  `,
  sectionTitle: css`
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: -0.48px;
    color: #141414;
    margin: 0;
  `,
  row: css`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  `,
  swatch: css`
    display: flex;
    flex-direction: column;
    width: 180px;
    box-shadow: 0px 2px 10px 0px rgba(75, 77, 78, 0.24);
    border-radius: 8px;
    overflow: hidden;
  `,
  colorBlock: css`
    height: 62px;
    width: 100%;
    border: 0.5px solid #d4d7dd;
    border-radius: 8px 8px 0 0;
    box-sizing: border-box;
  `,
  details: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: #fdfdfd;
    border-radius: 0 0 8px 8px;
  `,
  tokenName: css`
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: #898e9a;
    margin: 0;
  `,
  tokenValueRow: css`
    display: flex;
    gap: 16px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: #898e9a;
  `,
  tokenLabel: css`
    margin: 0;
  `,
  tokenValue: css`
    margin: 0;
  `,
}));

const RootToken: React.FC = () => {
  const { styles } = useStyles();

  return (
    <div className={styles.container}>
      {colorSections.map((section) => (
        <div key={section.title} className={styles.section}>
          <h3 className={styles.sectionTitle}>{section.title}</h3>
          <div className={styles.row}>
            {section.tokens.map((token) => (
              <div key={token.name} className={styles.swatch}>
                <div className={styles.colorBlock} style={{ backgroundColor: token.value }} />
                <div className={styles.details}>
                  <p className={styles.tokenName}>{token.name}</p>
                  <div className={styles.tokenValueRow}>
                    <p className={styles.tokenLabel}>Color</p>
                    <p className={styles.tokenValue}>{token.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RootToken;
